import { useEffect, useRef, useState, type Dispatch } from "react"
import { resolveAirportCode } from "@/lib/airports"
import {
  bookingHasProgressed,
  buildBookingSearch,
  canRestoreSearch,
  matchesToken,
  parseBookingParams,
  type BookingUrlParams,
} from "@/lib/urlState"
import type { BookingState, FlightOptionView } from "@/types/booking"
import type { HotelOption } from "@/lib/api/types"


/**
 * Keeps the page URL in step with the booking selection, and restores a
 * selection from a link someone was sent.
 *
 * Only the hosted page may do this. The same provider also backs the embeddable
 * widgets, and those run on a partner's own page — rewriting that page's URL is
 * never ours to do.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BookingDispatch = Dispatch<any>

/** Mirrors the current selection into the query string, without adding history entries. */
export function useBookingUrlWriter(state: BookingState, enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return
    if (state.eventStatus !== "success") return

    const search = buildBookingSearch(state)
    const next = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next === current) return

    window.history.replaceState(window.history.state, "", next)
  }, [enabled, state])
}

interface RestoreOptions {
  state: BookingState
  dispatch: BookingDispatch
  /** Runs the offer search, same as pressing Explore. */
  onSearch: () => void
  /**
   * Selects the hotel the same way a click does. It must be the page's own
   * handler, not a bare dispatch: choosing a hotel also moves the booking on
   * to the next step and scrolls there, and a restored link has to land the
   * reader in the same place a click would.
   */
  onSelectHotel: (hotel: HotelOption) => void
  onSelectFlight: (flight: FlightOptionView) => void
  enabled: boolean
}

/**
 * Replays a linked selection: form first, then the offer search, then the
 * hotel and flight once the offers that contain them have arrived.
 */
export function useBookingUrlRestore({
  state,
  dispatch,
  onSearch,
  onSelectHotel,
  onSelectFlight,
  enabled,
}: RestoreOptions) {
  const paramsRef = useRef<BookingUrlParams | null>(null)
  if (paramsRef.current === null) {
    paramsRef.current =
      enabled && typeof window !== "undefined"
        ? parseBookingParams(window.location.search)
        : {}
  }
  const params = paramsRef.current

  const [formApplied, setFormApplied] = useState(false)
  const searchStarted = useRef(false)
  const hotelApplied = useRef(false)
  const flightApplied = useRef(false)

  // The link's own fields. Order matters: the passenger count clamps add-on
  // quantities, and picking a stay period clears custom dates.
  useEffect(() => {
    if (!enabled || formApplied) return
    if (state.eventStatus !== "success" || !state.event) return
    // Only ever seeds an empty booking. The checkout replaces this page rather
    // than covering it, so coming back mounts it afresh and formApplied starts
    // at false again — applying the link a second time would dispatch
    // SET_STAY_PERIOD, and that clears the hotel, the flight and the add-ons
    // the traveller had just finished choosing.
    if (bookingHasProgressed(state)) return

    let active = true

    async function applyForm() {
      if (params.mode && state.event?.supportedModes) {
        const supported: Record<string, string> = {
          hotel: "hotel-only",
          hotel_flight: "flight-hotel",
          flight: "flight-only",
        }
        const allowed = state.event.supportedModes.map((m) => supported[m])
        if (allowed.includes(params.mode)) {
          dispatch({ type: "SET_PACKAGE_TYPE", packageType: params.mode })
        }
      }

      if (params.pax !== undefined) {
        dispatch({ type: "SET_PASSENGER_COUNT", count: params.pax })
      }

      if (params.departDate && params.returnDate) {
        dispatch({
          type: "SET_CUSTOM_FLIGHT_DATES",
          departureDate: params.departDate,
          returnDate: params.returnDate,
        })
      } else if (
        params.period !== undefined &&
        params.period < (state.event?.stayPeriods.length ?? 0)
      ) {
        dispatch({ type: "SET_STAY_PERIOD", index: params.period })
      }

      if (params.from) {
        const airports = await Promise.all(
          params.from.map((code) => resolveAirportCode(code))
        )
        if (!active) return
        for (const airport of airports) {
          if (airport) dispatch({ type: "ADD_DEPARTURE_AIRPORT", airport })
        }
      }


      if (active) setFormApplied(true)
    }

    void applyForm()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, formApplied, state.eventStatus, state.event])

  // Run the search the link implies, once the form it needs is in place.
  useEffect(() => {
    if (!enabled || !formApplied || searchStarted.current) return
    if (!canRestoreSearch(params)) return
    searchStarted.current = true
    onSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, formApplied])

  // The offer search overwrites both selections with its own default, so the
  // linked hotel and flight can only go back in once their offers have landed.
  useEffect(() => {
    if (!enabled || hotelApplied.current || !params.hotel) return
    if (state.offersStatus !== "success") return

    const hotel = state.offers?.hotelOptions.find((option) =>
      matchesToken(params.hotel!, option.allotmentId, option.name)
    )
    hotelApplied.current = true
    if (hotel && !hotel.soldOut) {
      onSelectHotel(hotel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, state.offersStatus, state.offers])

  useEffect(() => {
    if (!enabled || flightApplied.current || !params.flight) return
    if (state.flightOffersStatus !== "success") return

    const flight = state.flightOptions.find(
      (option) => option.selectionId === params.flight
    )
    flightApplied.current = true
    if (flight) {
      onSelectFlight(flight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, state.flightOffersStatus, state.flightOptions])

  return { restoring: canRestoreSearch(params) && !flightApplied.current }
}
